import { useQuery, useQueryClient } from 'react-query';

// API function - separate from hook for better testing/reusability
const fetchActiveCruises = async (baseUrl = '/api/v5') => {
  const response = await fetch(`${baseUrl}/cruises`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (!data.success || !data.cruise_lines) {
    throw new Error('Invalid response format');
  }

  return data.cruise_lines;
};

// New API function for fetching cruise data by partner_id
const fetchCruiseDataByPartnerId = async (partnerId) => {
  const sessionToken = Math.random().toString(36).substring(2) +
      (new Date()).getTime().toString(36);

  const params = new URLSearchParams({
      sessiontoken: sessionToken,
      db: window.gon?.env,
      brand: window.gon?.current_brand,
      cruise: 'true'
  });
  
  if (partnerId) {
      params.append('partner_id', partnerId);
  }

  const endpoint = 'https://y32qpijitwihjmtzbubk5lvaki0sqalh.lambda-url.us-east-1.on.aws/search';
  const apiEndpoint = `${endpoint}?${params.toString()}`;

  const response = await fetch(apiEndpoint, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data;
};

const useCruiseData = (baseUrl = '/api/v5', options = {}) => {
  const queryClient = useQueryClient();
  
  const {
    data: cruises = [],
    isLoading: loading,
    error,
    refetch,
    isError,
    isFetching,
    isStale,
    dataUpdatedAt,
  } = useQuery(
    ['cruises', baseUrl], // Query key
    () => fetchActiveCruises(baseUrl),
    {
      staleTime: 30 * 60 * 1000, // 30 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      ...options, // Allow custom options to override defaults
    }
  );

  // Helper functions
  const getCruiseById = (id) => {
    return cruises.find(cruise => cruise.id === id);
  };

  const getCruiseByPartnerId = (partnerId) => {
    return cruises.find(cruise => cruise.partner_id === partnerId);
  };

  // New method: Get cruise data for specific partner with React Query caching
  const useCruiseDataByPartnerId = (partnerId, queryOptions = {}) => {
    return useQuery(
      ['cruiseData', partnerId], // Unique query key for this partner
      () => fetchCruiseDataByPartnerId(partnerId),
      {
        enabled: !!partnerId, // Only run query if partnerId is provided
        staleTime: 30 * 60 * 1000, // 30 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        ...queryOptions, // Allow custom options to override defaults
      }
    );
  };

  // Force refresh - clears cache and refetches
  const refresh = () => {
    queryClient.invalidateQueries(['cruises', baseUrl]);
  };

  // Force refresh cruise data for specific partner
  const refreshCruiseDataByPartnerId = (partnerId) => {
    queryClient.invalidateQueries(['cruiseData', partnerId]);
  };

  // Prefetch function for performance
  const prefetchCruises = () => {
    queryClient.prefetchQuery(
      ['cruises', baseUrl],
      () => fetchActiveCruises(baseUrl),
      {
        staleTime: 30 * 60 * 1000,
      }
    );
  };

  // Prefetch cruise data for specific partner
  const prefetchCruiseDataByPartnerId = (partnerId) => {
    if (!partnerId) return;
    
    queryClient.prefetchQuery(
      ['cruiseData', partnerId],
      () => fetchCruiseDataByPartnerId(partnerId),
      {
        staleTime: 30 * 60 * 1000,
      }
    );
  };

  return {
    cruises,
    loading,
    error: error?.message || null,
    isError,
    isFetching,
    isStale,
    dataUpdatedAt,
    refetch,
    refresh,
    prefetchCruises,
    getCruiseById,
    getCruiseByPartnerId,
    // New methods for partner-specific cruise data
    useCruiseDataByPartnerId,
    refreshCruiseDataByPartnerId,
    prefetchCruiseDataByPartnerId,
    // Additional derived state
    isEmpty: cruises.length === 0,
    count: cruises.length,
  };
};

export default useCruiseData;